/**
 * API.js - API utility functions for making requests to the backend
 */
import Auth from '../Auth/Auth';

import BASE_URL from '../config' ;

// API base URL
const API_BASE_URL = BASE_URL;

/**
 * Make a fetch request with authentication
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
const fetchWithAuth = async (endpoint, options = {}) => {
  const headers = Auth.getAuthHeaders();

  const fetchOptions = {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {})
    }
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

  // Temporarily prevent auto logout for debugging
  if (response.status === 401) {
    console.warn('⚠️ Unauthorized (401). Token might be missing or invalid.');
    // Commenting these lines for now:
    // Auth.clearAuth();
    // window.location.href = '/login';
  }

  let data;
  try {
    data = await response.json();
  } catch (err) {
    console.error('❌ Failed to parse JSON:', err);
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    console.error('🚨 API Error Response:', data);
    throw new Error(data.message || 'An error occurred');
  }

  return data;
};

/**
 * Make a fetch request with authentication that returns a raw Response
 * (used for file downloads where we need the blob, not parsed JSON)
 */
const fetchRawWithAuth = async (endpoint, options = {}) => {
  const headers = Auth.getAuthHeaders();
  const fetchOptions = {
    ...options,
    headers: { ...headers, ...(options.headers || {}) },
  };
  const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response;
};


// Auth API calls
const authAPI = {
  /**
   * Login user
   * @param {Object} credentials - Login credentials (email/username and password)
   * @returns {Promise} - Login response
   */
  login: (credentials) => {
    return fetchWithAuth('/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
  },
  
  /**
   * Register a new company and admin
   * @param {Object} data - Registration data
   * @returns {Promise} - Registration response
   */
  register: (data) => {
    return fetch(`${API_BASE_URL}/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(res => res.json());
  },
  
  /**
   * Get current user profile
   * @returns {Promise} - User profile data
   */
  getUserProfile: () => {
    return fetchWithAuth('/profile/');
  },
  
  /**
   * Update password for current user
   * @param {Object} passwordData - Password update data
   * @returns {Promise} - Update response
   */
  updatePassword: (passwordData) => {
    return fetchWithAuth('/password/', {
      method: 'PUT',
      body: JSON.stringify(passwordData)
    });
  },
  
  /**
   * Update username for current user
   * @param {Object} usernameData - Username update data
   * @returns {Promise} - Update response
   */
  updateUsername: (usernameData) => {
    return fetchWithAuth('/username/', {
      method: 'PUT',
      body: JSON.stringify(usernameData)
    });
  },
  
  /**
   * Get all users in the company (admin only)
   * @returns {Promise} - List of users
   */
  getCompanyUsers: () => {
    return fetchWithAuth('/users/');
  },
  
  /**
   * Create a new user (admin only)
   * @param {Object} userData - User data
   * @returns {Promise} - Creation response
   */
  createUser: (userData) => {
    return fetchWithAuth('/users/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
  },

  /**
   * Update an existing user (admin only)
   * @param {number} userId - User ID
   * @param {Object} userData - User data
   * @returns {Promise} - Update response
   */
  updateUser: (userId, userData) => {
    return fetchWithAuth(`/users/${userId}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
  },
  
  /**
   * Delete a user (admin only)
   * @param {number} userId - User ID
   * @returns {Promise} - Deletion response
   */
  deleteUser: (userId) => {
    return fetchWithAuth(`/users/${userId}/`, {
      method: 'DELETE'
    });
  },
  
  /**
   * Get all user roles
   * @returns {Promise} - List of roles
   */
  getRoles: () => {
    return fetchWithAuth('/roles/');
  },
  
  /**
   * Sign out from all devices
   * @returns {Promise} - Sign out response
   */
  signOutAllDevices: () => {
    return fetchWithAuth('/logout-all/', {
      method: 'POST'
    });
  }
};

const companyAPI = {
  /**
   * Get company profile
   * @returns {Promise} - Company profile data
   */
  getCompanyProfile: () => {
    return fetchWithAuth('/company/profile/');
  },
  
  /**
   * Update company profile
   * @param {Object} profileData - Company profile data
   * @returns {Promise} - Update response
   */
  updateCompanyProfile: (profileData) => {
    return fetchWithAuth('/company/profile/', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profileData)
    });
  },
  
  /**
   * Get API configurations
   * @returns {Promise} - API configurations
   */
  getApiConfigurations: () => {
    return fetchWithAuth('/company/api-configurations/');
  },
  
  /**
   * Create new API configuration
   * @param {Object} configData - API configuration data
   * @returns {Promise} - Creation response
   */
  createApiConfiguration: (configData) => {
    return fetchWithAuth('/company/api-configurations/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(configData)
    });
  },
  
  /**
   * Update API configuration
   * @param {number} configId - Configuration ID
   * @param {Object} configData - API configuration data
   * @returns {Promise} - Update response
   */
  updateApiConfiguration: (configId, configData) => {
    return fetchWithAuth(`/company/api-configurations/${configId}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(configData)
    });
  },
  
  /**
   * Delete API configuration
   * @param {number} configId - Configuration ID
   * @returns {Promise} - Deletion response
   */
  deleteApiConfiguration: (configId) => {
    return fetchWithAuth(`/company/api-configurations/${configId}/`, {
      method: 'DELETE'
    });
  },
  
  /**
   * Update payment schedule
   * @param {Object} scheduleData - Payment schedule data
   * @returns {Promise} - Update response
   */
  updatePaymentSchedule: (scheduleData) => {
    return fetchWithAuth('/company/payment-schedule/', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scheduleData)
    });
  },
  
  /**
   * Manually process monthly charges for all tenants
   * @param {Object} options - Processing options
   * @returns {Promise} - Processing response
   */
  manualMonthlyProcessing: (options = {}) => {
    return fetchWithAuth('/tenants/manual-monthly-processing/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options)
    });
  },

  /**
   * Get notification preferences for the company
   * @returns {Promise} - Notification preferences data
   */
  getNotificationPreferences: () => {
    return fetchWithAuth('/company/notification-preferences/');
  },

  /**
   * Update notification preferences for the company
   * @param {Object} prefsData - Notification preferences data
   * @returns {Promise} - Update response
   */
  updateNotificationPreferences: (prefsData) => {
    return fetchWithAuth('/company/notification-preferences/', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefsData)
    });
  }
};

const onboardingAPI = {
  /**
   * Download the Excel onboarding template
   * Triggers a browser file-save (no apartment needed for the template).
   */
  downloadTemplate: async () => {
    const response = await fetchRawWithAuth('/tenants/onboard/template/');
    const blob = await response.blob();
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'tenant_onboarding_template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Validate an onboarding file without writing to the database.
   * @param {File}   file        - Excel or CSV file
   * @param {number} apartmentId - Target apartment ID
   * @returns {Promise<Object>}  - Dry-run result with column mapping + row count
   */
  validateFile: (file, apartmentId) => {
    const form = new FormData();
    form.append('file', file);
    return fetchWithAuth(
      `/tenants/onboard/${apartmentId}/?dry_run=true`,
      { method: 'POST', body: form }
    );
  },

  /**
   * Upload and process an onboarding file.
   * @param {File}     file        - Excel or CSV file
   * @param {number}   apartmentId - Target apartment ID
   * @param {Function} onProgress  - Optional callback(percent) — not supported by fetch,
   *                                 kept as a hook for XMLHttpRequest upgrade if needed.
   * @returns {Promise<Object>}    - { summary, results }
   */
  uploadFile: (file, apartmentId) => {
    const form = new FormData();
    form.append('file', file);
    return fetchWithAuth(
      `/tenants/onboard/${apartmentId}/`,
      { method: 'POST', body: form }
    );
  },
};

// Add this to the default export object
export default {
  fetchWithAuth,
  auth: authAPI,
  company: companyAPI,
  onboarding: onboardingAPI,
};